import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Card,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Popover,
  Select,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  LinearProgress,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { IoMdSearch } from "react-icons/io";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomDateRange from "./CustomDateRange";
import SkeletonLoader from "./SkeletonLoader";

const dateFilters = [
  "Today",
  "7 Days",
  "15 Days",
  "30 Days",
  "Custom Date",
];

const tableColumns = [
  { key: "source", label: "Source", minWidth: "105px" },
  { key: "max_date", label: "Max Date", minWidth: "110px", type: "date" },
  { key: "total_trx", label: "Total TRX", minWidth: "100px", type: "number" },
  { key: "total_nbrx", label: "Total NBRX", minWidth: "110px", type: "number" },
  { key: "record_count", label: "Record Count", minWidth: "108px", type: "number" },
  { key: "newly_added_nbrx", label: "New NBRX", minWidth: "95px", type: "number" },
  { key: "added_pharmparents", label: "Added Pharmparents", minWidth: "150px" },
  { key: "removed_nbrx", label: "Removed NBRX", minWidth: "118px", type: "number" },
  { key: "removed_pharmparents", label: "Removed Pharmparents", minWidth: "165px" },
  { key: "shifts_nbrx", label: "Shifted NBRX", minWidth: "112px", type: "number" },
  { key: "shifted_pharmparents", label: "Shifted Pharmparents", minWidth: "165px" },
];

const getDateRange = (selectedFilter, customRange) => {
  const today = dayjs().format("YYYY-MM-DD");

  if (selectedFilter === "Today") {
    return { run_date: today };
  }

  if (selectedFilter === "7 Days") {
    return {
      start_date: dayjs().subtract(6, "day").format("YYYY-MM-DD"),
      end_date: today,
    };
  }

  if (selectedFilter === "15 Days") {
    return {
      start_date: dayjs().subtract(14, "day").format("YYYY-MM-DD"),
      end_date: today,
    };
  }

  if (selectedFilter === "30 Days") {
    return {
      start_date: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
      end_date: today,
    };
  }

  if (selectedFilter === "Custom Date" && customRange.startDate && customRange.endDate) {
    return {
      start_date: customRange.startDate,
      end_date: customRange.endDate,
    };
  }

  return {};
};

const buildPharmaTrackerApiUrl = ({
  selectedBrand,
  selectedSources,
  selectedDateFilter,
  customRange,
}) => {
  const queryParams = [];
  const dateParams = getDateRange(selectedDateFilter, customRange);

  if (selectedBrand) {
    queryParams.push(`brand=${encodeURIComponent(selectedBrand)}`);
  }

  if (selectedSources.length === 1) {
    queryParams.push(`source=${encodeURIComponent(selectedSources[0])}`);
  }

  if (dateParams.run_date) {
    queryParams.push(`run_date=${dateParams.run_date}`);
  }

  if (dateParams.start_date && dateParams.end_date) {
    queryParams.push(`start_date=${dateParams.start_date}`);
    queryParams.push(`end_date=${dateParams.end_date}`);
  }

  return `/pharma-tracker/${queryParams.length > 0 ? `?${queryParams.join("&")}` : ""}`;
};

const formatValue = (value, type) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (type === "date") {
    return dayjs(value).isValid() ? dayjs(value).format("MMM DD, YYYY") : value;
  }

  if (type === "number") {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return "—";
    }

    return Number.isNaN(numericValue)
      ? "—"
      : numericValue.toLocaleString("en-US");
  }

  return value;};

const isTodayDate = (value) => dayjs(value).isSame(dayjs(), "day");

const filterHistoryDataBySources = (data, selectedSources) => {
  if (!selectedSources.length) {
    return data;
  }

  return (data || [])
    .map((record) => ({
      ...record,
      entries: (record?.entries || []).filter((entry) =>
        selectedSources.includes(entry?.source)
      ),
    }))
    .filter((record) => record.entries.length > 0);
};

const PharmaHistoryTab = () => {
  const [historyData, setHistoryData] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sources, setSources] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
const [selectedSources, setSelectedSources] = useState([]);
  const [selectedDateFilter, setSelectedDateFilter] = useState("Today");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState(null);
  const [sourcesMenuOpen, setSourcesMenuOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteRow, setSelectedDeleteRow] = useState(null);
  const [descModalOpen, setDescModalOpen] = useState(false);
  const [selectedDescRecord, setSelectedDescRecord] = useState(null);
  const [editedDescription, setEditedDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [customRange, setCustomRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [loading, setLoading] = useState(false);
  const [rangeError, setRangeError] = useState("");

  const cookies = document.cookie.split("; ").reduce((acc, cookie) => {
    const [name, value] = cookie.split("=");
    acc[name] = decodeURIComponent(value);
    return acc;
  }, {});

  const totalEntries = historyData.reduce(
    (count, item) => count + (item?.entries?.length || 0),
    0
  );

  const areAllSourcesSelected =
    sources.length > 0 &&
    (selectedSources.length === 0 || selectedSources.length === sources.length);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPharmaDetails = async () => {
      try {
        const response = await axios.get("/pharma-details/");
        setBrands(response?.data?.data?.brands || []);
        setSources(response?.data?.data?.sources || []);
      } catch (error) {
        console.error("Error fetching pharma details:", error);
      }
    };

    fetchPharmaDetails();
  }, []);

  useEffect(() => {
    const fetchPharmaHistory = async () => {
      const isCustomRange = selectedDateFilter === "Custom Date";
      const selectedCustomRange = {
        startDate: customRange.startDate,
        endDate: customRange.endDate,
      };

      if (
        isCustomRange &&
        (!selectedCustomRange.startDate || !selectedCustomRange.endDate)
      ) {
        setLoading(false);
        setRangeError("");
        return;
      }

      if (
        isCustomRange &&
        dayjs(selectedCustomRange.endDate).isBefore(
          dayjs(selectedCustomRange.startDate)
        )
      ) {
        setHistoryData([]);
        setLoading(false);
        setRangeError("End date cannot be earlier than start date.");
        return;
      }

      setRangeError("");
      setLoading(true);

      try {
        const apiUrl = buildPharmaTrackerApiUrl({
          selectedBrand,
          selectedSources,
          selectedDateFilter,
          customRange: selectedCustomRange,
        });
        const response = await axios.get(apiUrl);
        setHistoryData(
          filterHistoryDataBySources(response?.data?.data || [], selectedSources)
        );
      } catch (error) {
        console.error("Error fetching pharma tracker history:", error);
        setHistoryData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPharmaHistory();
  }, [
    selectedBrand,
    selectedSources,
    selectedDateFilter,
    customRange.startDate,
    customRange.endDate,
  ]);

  const handleDateFilterChange = (newValue, anchorElement = null) => {
    setSelectedDateFilter(newValue);

    if (newValue === "Custom Date") {
      setDatePickerAnchorEl(anchorElement);
      setShowDatePicker(true);
      return;
    }

    setShowDatePicker(false);
    setDatePickerAnchorEl(null);
    if (selectedDateFilter === "Custom Date") {
      setCustomRange({
        startDate: "",
        endDate: "",
      });
      setRangeError("");
    }
  };

  const handleDeleteRow = (record) => {
    const ids = (record?.entries || []).map((entry) => entry?.id).filter(Boolean);

    if (!ids.length) {
      return;
    }

    setSelectedDeleteRow({
      ids,
      run_date: record?.run_date,
      brand: record?.brand,
    });
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteRow(null);
  };

  const handleDescriptionOpen = (record) => {
    setSelectedDescRecord(record);
    const remarks = record?.remarks || "";
    setEditedDescription(remarks);
    // Only auto-edit if it is today AND remarks are empty
    setIsEditingDesc(isTodayDate(record?.run_date) && remarks === "");
    setDescModalOpen(true);
  };

  const handleDescriptionClose = () => {
    setDescModalOpen(false);
    setSelectedDescRecord(null);
    setEditedDescription("");
    setIsEditingDesc(false);
  };

  const handleDescriptionSave = async () => {
    if (!selectedDescRecord) return;

    setSavingDescription(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "X-CSRFToken": cookies.csrftoken,
      };

      await axios.put(
        `/pharma-tracker/`,
        {
          run_date: selectedDescRecord.run_date,
          brand: selectedDescRecord.brand,
          remarks: editedDescription,
        },
        { headers }
      );

      toast.success("Remarks updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Refresh data
      const apiUrl = buildPharmaTrackerApiUrl({
        selectedBrand,
        selectedSources,
        selectedDateFilter,
        customRange,
      });
      const response = await axios.get(apiUrl);
      setHistoryData(
        filterHistoryDataBySources(response?.data?.data || [], selectedSources)
      );
      handleDescriptionClose();
    } catch (error) {
      console.error("Error updating description:", error);
      toast.error("Error updating description!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setSavingDescription(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDeleteRow?.ids?.length) {
      handleDeleteClose();
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        "X-CSRFToken": cookies.csrftoken,
      };

      await Promise.all(
        selectedDeleteRow.ids.map((id) =>
          axios.delete(`/delete-pharma-tracker/${id}/`, {
            headers,
          })
        )
      );

      toast.success(
        selectedDeleteRow.ids.length > 1
          ? "Records deleted successfully!"
          : "Record deleted successfully!",
        {
        position: "top-right",
        autoClose: 3000,
        }
      );

const apiUrl = buildPharmaTrackerApiUrl({
  selectedBrand,
  selectedSources,
  selectedDateFilter,
  customRange,
});
      const response = await axios.get(apiUrl);
      setHistoryData(
        filterHistoryDataBySources(response?.data?.data || [], selectedSources)
      );
      handleDeleteClose();
    } catch (error) {
      console.error("Error deleting pharma tracker record:", error);
      toast.error("Error deleting record!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const emptyMessage =
    selectedDateFilter === "Custom Date" &&
    (!customRange.startDate || !customRange.endDate)
      ? "Please select both start and end dates"
      : rangeError || "No Records Available";

  const handleCustomRangeFetch = (range) => {
    setCustomRange({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const handleSourcesChange = (event) => {
    const { value } = event.target;
    setSelectedSources(
      typeof value === "string"
        ? value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : value
    );
  };

  const handleSourcesOpen = () => {
    setSourcesMenuOpen(true);
  };

  const handleSourcesClose = () => {
    setSourcesMenuOpen(false);
  };

  const handleDatePickerClose = () => {
    setShowDatePicker(false);
    setDatePickerAnchorEl(null);
  };

  return (
    <div
      style={{
        padding: "0px 0px",
      }}
    >
      <Card
        elevation={0}
        sx={{
          p: "18px 20px",
          borderRadius: "16px",
          // marginX: "1%",
          background: "#fff",
          // border: "1px solid #f0f2f7",
        }}
      >
        <Box
          id="frequency_selector"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "18px",
            }}
          >
                      <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "nowrap",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#101828",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                mb: 0,
              }}
            >
              History
            </Typography>

            <Box
              sx={{
                minWidth: "28px",
                height: "22px",
                mt: "2px",
                px: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#6c8bdb",
                color: "#fcfcfc",
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: 1,
                borderRadius: "999px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {totalEntries.toLocaleString("en-US")}
            </Box>
          </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", lg: "flex-end" },
                gap: "10px",
                flex: "1 1 620px",
                minWidth: "300px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", lg: "flex-end" },
                  flexWrap: "wrap",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "2px",
                    p: "3px",
                    background: "#f4f6fb",
                    borderRadius: "12px",
                    border: "1px solid #eef1f6",
                    minHeight: "38px",
                    flexWrap: "wrap",
                  }}
                >
                  {dateFilters.map((filterLabel) => {
                    const isSelected = selectedDateFilter === filterLabel;

                    return (
                      <Button
                        key={filterLabel}
                        variant="text"
                        onClick={(event) =>
                          handleDateFilterChange(
                            filterLabel,
                            filterLabel === "Custom Date"
                              ? event.currentTarget
                              : null
                          )
                        }
                        sx={{
                          minWidth:
                            filterLabel === "Today"
                              ? "72px"
                              : filterLabel === "Custom Date"
                              ? "108px"
                              : "78px",
                          height: "30px",
                          px: "14px",
                          borderRadius: "8px",
                          color: isSelected ? "#0576FC" : "#7b8798",
                          background: isSelected ? "#fff" : "transparent",
                          boxShadow: isSelected
                            ? "0px 1px 4px rgba(15, 23, 42, 0.08)"
                            : "none",
                          fontSize: "12px",
                          fontWeight: 700,
                          textTransform: "none",
                          "&:hover": {
                            background: isSelected ? "#fff" : "transparent",
                          },
                        }}
                      >
                        {filterLabel}
                      </Button>
                    );
                  })}
                </Box>

                <Popover
                  open={selectedDateFilter === "Custom Date" && showDatePicker}
                  anchorEl={datePickerAnchorEl}
                  onClose={handleDatePickerClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      borderRadius: "8px",
                      boxShadow: "2px 2px 8px 0px rgba(0,0,0,0.30)",
                      overflow: "visible",
                    },
                  }}
                >
                  <CustomDateRange
                    range={customRange}
                    showDatePicker={showDatePicker}
                    setShowDatePicker={setShowDatePicker}
                    usePopover
                    onFetch={handleCustomRangeFetch}
                  />
                </Popover>

        <FormControl
          variant="outlined"
          sx={{
            minWidth: 140,
            maxWidth: 240,
            "& .MuiOutlinedInput-root": {
              height: "36px",
              background: "#fff",
              borderRadius: "8px",
            },
                  }}
                >
                  <Select
                    size="small"
                    value={selectedBrand}
                    onChange={(event) => setSelectedBrand(event.target.value)}
                    displayEmpty
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: selectedBrand ? "#1f2937" : "#3f434a",
                    }}
                  >
                    <MenuItem value="">All Brands</MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand} value={brand}>
                        {brand}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  sx={{
                    minWidth: 150,
                    maxWidth: 180,
                    "& .MuiOutlinedInput-root": {
                      height: "36px",
                      background: "#fff",
                      borderRadius: "8px",
                    },
                  }}
                >
          <Select
            multiple
            size="small"
            open={sourcesMenuOpen}
            onOpen={handleSourcesOpen}
            onClose={handleSourcesClose}
            value={selectedSources}
            onChange={handleSourcesChange}
            displayEmpty
            IconComponent={KeyboardArrowDownIcon}
            renderValue={(selected) => {
              const selectedValues = Array.isArray(selected)
                ? selected
                : typeof selected === "string"
                ? selected
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                : [];

              if (selectedValues.length === 0 || (sources.length > 0 && selectedValues.length === sources.length)) {
                return "All Sources";
              }
              if (selectedValues.length === 1) return selectedValues[0];
              return `${selectedValues.length} Sources`;
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 260,
                  "& .MuiMenuItem-root": {
                    minHeight: 34,
                  },
                },
              },
            }}
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: selectedSources.length ? "#1f2937" : "#3f434a",
              "& .MuiSelect-select": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
        >
          {sources.map((source) => (
            <MenuItem key={source} value={source}>
              <Checkbox checked={selectedSources.indexOf(source) > -1} size="small" />
              <ListItemText primary={source} />
            </MenuItem>
          ))}
        </Select>
                </FormControl>
              </Box>

              {rangeError && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#d32f2f",
                    fontWeight: 500,
                    width: "100%",
                    textAlign: { xs: "left", lg: "right" },
                  }}
                >
                  {rangeError}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Card>

      <Box
        sx={{
          border: "none",
          boxShadow: "none",
          marginTop: "14px",
          // marginX: "1%",
          borderRadius: "16px",
          padding: "0px",
          position: 'relative',
        }}
      >
        {/* Linear Progress for background updates */}
        {loading && historyData.length > 0 && (
          <LinearProgress 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              height: '3px',
              borderRadius: '16px 16px 0 0',
              zIndex: 10
            }} 
          />
        )}

        {loading && historyData.length === 0 ? (
          <Box
            sx={{
              minHeight: "260px",
            }}
          >
            <SkeletonLoader type="pharmaHistoryCards" cardCount={2} />
          </Box>
        ) : historyData.length === 0 ? (
          <Box
            sx={{
              minHeight: "260px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: '#fff',
              borderRadius: '16px',
              border: '1px dashed #E2E8F0',
            }}
          >
            <Box
              sx={{
                width: "100%",
                color: "#94A3B8",
                textAlign: "center",
                padding: "0 30px",
              }}
            >
              <Box sx={{ fontSize: "80px", color: '#E2E8F0', mb: 1 }}>
                <IoMdSearch />
              </Box>
              <Typography sx={{ color: '#64748B', fontWeight: 500 }}>{emptyMessage}</Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            {historyData.map((record, index) => {
              const canShowDelete = isTodayDate(record?.run_date) && areAllSourcesSelected;

              return (
              <Card
                key={`${record?.run_date}-${record?.brand}-${index}`}
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #eaeaea",
                  marginBottom: index === historyData.length - 1 ? "0px" : "12px",
                  overflow: "hidden",
                }}
              >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                  px: "16px",
                  py: "12px",
                  borderBottom: "1px solid #eaeaea",
                  background: "#fff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarMonthOutlinedIcon
                    sx={{ fontSize: "18px", color: "#6a6b6d" }}
                  />
                  <Typography sx={{ fontWeight: "550", fontSize: "14px" }}>
                    {dayjs(record?.run_date).format("MMM DD, YYYY")}
                  </Typography>
                  <Box
                    sx={{
                      background: "#0576FC",
                      color: "#fff",
                      borderRadius: "7px",
                      fontSize: "11px",
                      fontWeight: 700,
                      px: 1,
                      py: "3px",
                      textTransform: "uppercase",
                    }}
                  >
                    {record?.brand}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDescriptionOpen(record)}
                    sx={{
                      color: "#6c8bdb",
                      padding: "4px",
                      "&:hover": {
                        background: "#EFF6FF",
                      },
                    }}
                    title="View Remarks"
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: "18px" }} />
                  </IconButton>

                  {canShowDelete ? (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleDeleteRow(record)}
                      startIcon={
                        <DeleteOutlineOutlinedIcon sx={{ fontSize: "15px" }} />
                      }
                      sx={{
                        color: "#d32f2f",
                        fontSize: "12px",
                        minWidth: "0px",
                        padding: 0,
                        textTransform: "none",
                        justifyContent: "flex-end",
                        "&:hover": {
                          background: "transparent",
                        },
                      }}
                    >
                      Delete
                    </Button>
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ px: "14px", py: "12px", background: "#fff" }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      minWidth: "1280px",
                    }}
                  >
                    <Box component="thead">
                      <Box component="tr">
                        {tableColumns.map((column) => (
                          <Box
                            key={column.key}
                            component="th"
                            sx={{
                              minWidth: column.minWidth,
                              padding: "10px 8px",
                              borderBottom: "1px solid #eaeaea",
                              color: "#8b95a7",
                              fontSize: "10px",
                              fontWeight: 700,
                              textAlign: "left",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                            }}
                          >
                            {column.label}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    <Box component="tbody">
                      {record?.entries?.map((entry) => (
                        <Box component="tr" key={entry?.id}>
                          {tableColumns.map((column) => {
                            const displayValue = formatValue(
                              entry?.[column.key],
                              column.type
                            );

                            return (
                              <Box
                                key={`${entry?.id}-${column.key}`}
                                component="td"
                                sx={{
                                  padding: "12px 8px",
                                  borderBottom: "1px solid #f3f4f6",
                                  fontSize: "12.5px",
                                  color: "#333",
                                  verticalAlign: "top",
                                }}
                              >
                                  <span
                                    title={displayValue}
                                    style={{
                                      display: "inline-block",
                                      maxWidth: column.minWidth,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {column.key === "source" ? (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          bgcolor: "#FFF7ED",
                                          color: "#EA580C",
                                          px: 1,
                                          py: 0.3,
                                          borderRadius: "6px",
                                          fontSize: "11px",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          border: "1px solid #FFEDD5",
                                        }}
                                      >
                                        {displayValue}
                                      </Box>
                                    ) : (
                                      displayValue
                                    )}
                                  </span>
                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Card>
            );
          })}
        </Box>
      )}
    </Box>
      <ToastContainer
        toastOptions={{
          style: {
            fontSize: "20px",
            padding: "16px",
            width: "350px",
          },
        }}
      />
      <Modal
        open={deleteModalOpen}
        onClose={handleDeleteClose}
        aria-labelledby="delete-pharma-history-modal"
        aria-describedby="delete-pharma-history-description"
        disableAutoFocus
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.1)",
            width: 400,
            overflow: "hidden",
            outline: "none"
          }}
        >
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Box
              sx={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                bgcolor: "#EFF6FF",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2.5
              }}
            >
              <DeleteOutlineOutlinedIcon sx={{ width: "26px", height: "26px", color: "#2563EB" }} />
            </Box>
            
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', mb: 1.5, fontSize: '1.25rem' }}>
              Delete Entry
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.6, px: 2, fontSize: '14px', mb: 4 }}>
              Are you sure you want to delete this entry? This action cannot be undone.
            </Typography>

            <Box sx={{ width: '100%', bgcolor: '#F8FAFC', borderRadius: '12px', p: 2.5, mb: 4, display: 'flex', justifyContent: 'space-between', textAlign: 'left' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>RUN DATE</Typography>
                <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600 }}>
                  {selectedDeleteRow?.run_date ? dayjs(selectedDeleteRow.run_date).format("MMM DD, YYYY") : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>BRAND</Typography>
                <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600 }}>
                  {selectedDeleteRow?.brand || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>ENTRIES</Typography>
                <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600 }}>
                  {selectedDeleteRow?.ids?.length || "-"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ width: '100%', display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleDeleteClose}
                disableElevation
                sx={{
                  flex: 1,
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#F1F5F9",
                  color: "#334155",
                  py: 1.5,
                  '&:hover': { bgcolor: "#E2E8F0" }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleDeleteConfirm}
                disableElevation
                sx={{
                  flex: 1,
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#2563EB",
                  color: "#FFFFFF",
                  py: 1.5,
                  '&:hover': { bgcolor: "#1D4ED8" }
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>
        </Modal>
      <Modal
        open={descModalOpen}
        onClose={handleDescriptionClose}
        aria-labelledby="remarks-pharma-history-modal"
        disableAutoFocus
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#ffffff",
            borderRadius: "14px",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
            width: 440,
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 1.8,
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: "20px", color: "#2563EB" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1E293B" }}>
                Remarks
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleDescriptionClose}
              sx={{
                color: "#94A3B8",
                padding: "4px",
                "&:hover": { bgcolor: "#F1F5F9", color: "#64748B" },
              }}
            >
              <CloseIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ px: 2.5, py: 2.5 }}>
            {/* Date & Brand chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  bgcolor: "#EFF6FF",
                  color: "#2563EB",
                  px: 1.5,
                  py: 0.8,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  border: "1px solid #DBEAFE",
                }}
              >
                <CalendarMonthOutlinedIcon sx={{ fontSize: "16px" }} />
                {dayjs(selectedDescRecord?.run_date).format("MMM DD, YYYY")}
              </Box>
              <Box
                sx={{
                  bgcolor: "#F0FDF4",
                  color: "#16A34A",
                  px: 1.5,
                  py: 0.8,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 700,
                  border: "1px solid #DCFCE7",
                  textTransform: "uppercase",
                }}
              >
                {selectedDescRecord?.brand}
              </Box>
            </Box>

            {!isEditingDesc && editedDescription ? (
              // View Mode (Premium Note style)
              <Box
                sx={{
                  bgcolor: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  borderLeft: "4px solid #2563EB",
                  p: "16px 20px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 0.5,
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.04)"
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "#94A3B8",
                    letterSpacing: "0.5px",
                    mb: 0.5,
                  }}
                >
                  Record Remarks
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    lineHeight: 1.6,
                    color: "#334155",
                    fontWeight: 500,
                  }}
                >
                  {editedDescription}
                </Typography>
                
                {isTodayDate(selectedDescRecord?.run_date) && (
                  <IconButton
                    size="small"
                    onClick={() => setIsEditingDesc(true)}
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      color: "#94A3B8",
                      "&:hover": { bgcolor: "#F1F5F9", color: "#2563EB" },
                    }}
                    title="Edit Remarks"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </IconButton>
                )}
              </Box>
            ) : !isTodayDate(selectedDescRecord?.run_date) && !editedDescription ? (
              // Past date + No Remarks
              <Box
                sx={{
                  bgcolor: "#F8FAFC",
                  borderRadius: "10px",
                  border: "1px dashed #E2E8F0",
                  p: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1
                }}
              >
                <DescriptionOutlinedIcon sx={{ color: "#CBD5E1", fontSize: "28px" }} />
                <Typography sx={{ fontSize: "13px", color: "#94A3B8", fontWeight: 500 }}>
                  No remarks recorded for this date.
                </Typography>
              </Box>
            ) : (
              // Edit Mode (Only for today or when editing initiated)
              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter remarks here..."
                  value={editedDescription}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setEditedDescription(e.target.value);
                    }
                  }}
                  autoFocus
                  inputProps={{ maxLength: 500 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontSize: "14px",
                      bgcolor: "#ffffff",
                      "& fieldset": { borderColor: editedDescription.length >= 500 ? "#d32f2f" : "#E2E8F0" },
                      "&.Mui-focused fieldset": { 
                        borderColor: editedDescription.length >= 500 ? "#d32f2f" : "#2563EB", 
                        borderWidth: "1.5px" 
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                        lineHeight: 1.65,
                        color: "#334155"
                    }
                  }}
                />
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  mt: 0.8, 
                  px: 0.5 
                }}>
                  <Typography 
                    sx={{ 
                      fontSize: "11px", 
                      color: editedDescription.length >= 500 ? "#d32f2f" : "#94A3B8",
                      fontWeight: editedDescription.length >= 500 ? 600 : 400
                    }}
                  >
                    {editedDescription.length >= 500 ? "Maximum characters reached" : ""}
                  </Typography>
                  {/* <Typography 
                    sx={{ 
                      fontSize: "11px", 
                      color: editedDescription.length >= 500 ? "#d32f2f" : "#94A3B8",
                      fontWeight: 500
                    }}
                  >
                    {editedDescription.length}/500
                  </Typography> */}
                </Box>
              </Box>
            )}

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 3,
              }}
            >
              {(isEditingDesc || !editedDescription) && isTodayDate(selectedDescRecord?.run_date) ? (
                <>
                  <Button
                    onClick={handleDescriptionClose}
                    sx={{
                      textTransform: "none",
                      color: "#64748B",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleDescriptionSave}
                    disabled={savingDescription}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#2563EB",
                      px: 4,
                      py: 1,
                      borderRadius: "8px",
                      fontWeight: 600,
                      boxShadow: "0px 4px 12px rgba(37, 99, 235, 0.2)",
                      "&:hover": { bgcolor: "#1D4ED8" },
                      "&.Mui-disabled": { bgcolor: "#93B5F5", color: "#fff" }
                    }}
                  >
                    {savingDescription ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleDescriptionClose}
                  sx={{
                    textTransform: "none",
                    color: "#2563EB",
                    fontWeight: 700,
                    px: 4,
                    py: 1,
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#EFF6FF" }
                  }}
                >
                  Close
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default PharmaHistoryTab;
